import { useEffect, useRef, useState, useCallback } from "react";
import { Paper, Box, CircularProgress } from "@mui/material";
import theme from "../../styles/Themecolors";

/**
 * Custom infinite scroll hook specifically for Opening Stock dropdown
 * Provides smooth scrolling with preserved scroll position and proper search term handling
 */
const useInfiniteScrollOpeningStock = (fetchData, row, setRow, searchTermRef) => {
   
  const isFetchingRef = useRef(false);
  const scrollPositionRef = useRef(0);
  const listboxRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastSearchTermRef = useRef('');
  const hasMoreDataRef = useRef(true);
   
  const handleScroll = useCallback((event) => {
    const listboxNode = event.target;
    listboxRef.current = listboxNode;
    
    // Store current scroll position for preservation
    scrollPositionRef.current = listboxNode.scrollTop;
    
    // More reliable bottom detection with buffer
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;
    
    // Check if user is near the bottom (within 100px buffer for better UX)
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    // Only fetch if near bottom, not already fetching, and we believe there's more data
    if (isNearBottom && !isFetchingRef.current && hasMoreDataRef.current) {
      isFetchingRef.current = true;
      setIsLoading(true);
      
      // Get current search term from ref
      const currentSearchTerm = searchTermRef?.current || '';
      
      setRow((prev) => {
        const newRow = prev + 20;
        
        // Call fetchData with the current search term from ref
        fetchData(currentSearchTerm, newRow, 20);
        
        return newRow;
      });
    }
  }, [fetchData, searchTermRef, setRow]);
  
  // Reset fetching flag when loading completes and restore scroll position
  useEffect(() => {
    if (!isLoading && isFetchingRef.current) {
      // Use a small delay to ensure DOM has updated
      setTimeout(() => {
        isFetchingRef.current = false;
        
        // Restore scroll position after data load to prevent jumps
        if (listboxRef.current && scrollPositionRef.current > 0) {
          // Use requestAnimationFrame for smoother scroll restoration
          requestAnimationFrame(() => {
            if (listboxRef.current) {
              listboxRef.current.scrollTop = scrollPositionRef.current;
            }
          });
        }
      }, 50);
    }
  }, [isLoading]);
  
  // Reset pagination when search term changes
  useEffect(() => {
    const currentSearchTerm = searchTermRef?.current || '';
    if (currentSearchTerm !== lastSearchTermRef.current) {
      lastSearchTermRef.current = currentSearchTerm;
      hasMoreDataRef.current = true; // Reset flag when search changes
      // Reset scroll position when search changes
      scrollPositionRef.current = 0;
    }
  }, [searchTermRef]);
  
  const ListboxProps = {
    onScroll: handleScroll,
    sx: {
      maxHeight: "300px",
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.default}`, // Firefox
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.background.default,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
      scrollBehavior: "smooth", // Enable smooth scrolling
      paddingTop: "8px", // Ensure first item has space from top
      paddingBottom: "4px", // Ensure last item has space from bottom
      // Prevent scroll jumping
      overscrollBehavior: "contain",
    },
  };

  const PaperComponent = ({ children, ...props }) => (
    <Paper
      {...props}
      sx={{
        maxWidth: '455px',
        width: 'fit-content', // grows with content
        minWidth: '100%',     // match input width
        overflow: 'visible',  // Allow dropdown to expand naturally
        maxHeight: 'none',    // Remove height constraint to prevent jumps
      }}
    >
      {children}
    </Paper>
  );

  // Expose method to set hasMoreData flag
  const setHasMoreData = useCallback((hasMore) => {
    hasMoreDataRef.current = hasMore;
  }, []);

  return { setIsLoading, ListboxProps, PaperComponent, setHasMoreData };
};

export default useInfiniteScrollOpeningStock;
